import { createContext, useContext, useState, useCallback, useMemo, type ReactNode } from 'react';
import type { MediaAlert, FiredAlertPayload, PlayerAlertSettings, AlertLayout, AlertTrigger } from '@roll1/shared';
import { useAlertQueue } from '../hooks/useAlertQueue';
import { useSessionContext } from './SessionContext';

const STORAGE_KEY = 'roll1-alert-settings';

const DEFAULT_SETTINGS: PlayerAlertSettings = {
  muted: false,
  volume: 100,
};

function loadSettings(): PlayerAlertSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        muted: typeof parsed.muted === 'boolean' ? parsed.muted : false,
        volume: typeof parsed.volume === 'number' ? parsed.volume : 100,
      };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_SETTINGS };
}

function saveSettings(settings: PlayerAlertSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore
  }
}

export interface AlertContextValue {
  alerts: MediaAlert[];
  setAlerts: React.Dispatch<React.SetStateAction<MediaAlert[]>>;
  playerSettings: PlayerAlertSettings;
  setPlayerSettings: (settings: PlayerAlertSettings) => void;
  createAlert: (alert: {
    name: string;
    visualUrl: string | null;
    visualType: 'gif' | 'webm' | 'image' | null;
    soundUrl: string | null;
    durationMs: number;
    layout: AlertLayout;
    volume: number;
    trigger: AlertTrigger;
    triggerEnabled: boolean;
    preset: string | null;
  }) => void;
  updateAlert: (alertId: string, changes: Partial<Omit<MediaAlert, 'id' | 'sortOrder'>>) => void;
  deleteAlert: (alertId: string) => void;
  triggerAlert: (alertId: string) => void;
  firePreset: (presetId: string) => void;
  reorderAlerts: (alertIds: string[]) => void;
  queue: ReturnType<typeof useAlertQueue>;
}

const AlertContext = createContext<AlertContextValue | null>(null);

export function AlertProvider({ children }: { children: ReactNode }) {
  const { mp } = useSessionContext();
  const [alerts, setAlerts] = useState<MediaAlert[]>([]);
  const [playerSettings, setPlayerSettingsState] = useState<PlayerAlertSettings>(loadSettings);

  const queue = useAlertQueue();

  const setPlayerSettings = useCallback((settings: PlayerAlertSettings) => {
    setPlayerSettingsState(settings);
    saveSettings(settings);
  }, []);

  const createAlert = useCallback((alert: {
    name: string;
    visualUrl: string | null;
    visualType: 'gif' | 'webm' | 'image' | null;
    soundUrl: string | null;
    durationMs: number;
    layout: AlertLayout;
    volume: number;
    trigger: AlertTrigger;
    triggerEnabled: boolean;
    preset: string | null;
  }) => {
    if (mp.session) {
      mp.send({ type: 'alert:create', alert });
    }
  }, [mp]);

  const updateAlert = useCallback((alertId: string, changes: Partial<Omit<MediaAlert, 'id' | 'sortOrder'>>) => {
    if (mp.session) {
      mp.send({ type: 'alert:update', alertId, changes });
    }
  }, [mp]);

  const deleteAlert = useCallback((alertId: string) => {
    if (mp.session) {
      mp.send({ type: 'alert:delete', alertId });
    }
  }, [mp]);

  const triggerAlert = useCallback((alertId: string) => {
    if (mp.session) {
      mp.send({ type: 'alert:trigger', alertId });
    }
  }, [mp]);

  const firePreset = useCallback((presetId: string) => {
    if (mp.session) {
      mp.send({ type: 'alert:fire-preset', presetId });
    }
  }, [mp]);

  const reorderAlerts = useCallback((alertIds: string[]) => {
    if (mp.session) {
      mp.send({ type: 'alert:reorder', alertIds });
    }
  }, [mp]);

  const value = useMemo(
    () => ({
      alerts,
      setAlerts,
      playerSettings,
      setPlayerSettings,
      createAlert,
      updateAlert,
      deleteAlert,
      triggerAlert,
      firePreset,
      reorderAlerts,
      queue,
    }),
    [alerts, playerSettings, setPlayerSettings, createAlert, updateAlert, deleteAlert, triggerAlert, firePreset, reorderAlerts, queue],
  );

  return (
    <AlertContext.Provider value={value}>
      {children}
    </AlertContext.Provider>
  );
}

export function useAlertContext() {
  const ctx = useContext(AlertContext);
  if (!ctx) throw new Error('useAlertContext must be used within AlertProvider');
  return ctx;
}
